import type { User as Auth0User } from "@auth0/auth0-react";
import { atom } from "jotai";

import { generateShortUuid } from "@moment/api-collab";
import { Logger } from "@moment/logging";

export type GetAccessTokenSilently = {
	(): Promise<string | undefined>;
};

export type Auth0AtomContext = {
	isAuthenticated: boolean;
	loggedInUser: Auth0User | null;
};

// This is dumb but atoms cannot store functions.
// see https://github.com/pmndrs/jotai/discussions/1824
type TokenFunction = {
	getAccessTokenSilently: GetAccessTokenSilently | undefined;
	debuggingFunction: GetAccessTokenSilently | undefined;
	traceId: string;
};

const $draftGetAccessTokenSilently = atom<TokenFunction>({
	getAccessTokenSilently: undefined,
	debuggingFunction: undefined,
	traceId: "",
});

$draftGetAccessTokenSilently.debugLabel = "draftGetAccessTokenSilently";

const logger = new Logger("AuthTokenAtom");

export const $accessTokenFunc = atom(
	(get) => get($draftGetAccessTokenSilently),
	(get, set, input: { getAccessTokenSilently?: GetAccessTokenSilently; traceId?: string }) => {
		const traceId = input?.traceId || generateShortUuid();
		const update = input.getAccessTokenSilently;
		logger.info(`[${traceId}] Updating getAccessTokenSilently function in atom.`, {
			hasFunction: !!update,
			update,
		});
		let current = get($draftGetAccessTokenSilently);
		if (update) {
			current.getAccessTokenSilently = async function () {
				//
				// IMPORTANT: Break up this thread to decrease probability it blocks the render
				// thread for more than 16ms (one frame a 60fps). This is a heavily-trafficked
				// function.
				//
				// The primary issue here is that `getAccessTokenSilently` does a bunch of work
				// before actually deciding to suspend and fetch the access token—much more than
				// 16ms, which is the entire perf budget for a single 60fps render cycle. In
				// creating a formal suspense point, we "chunk" this work up, and give ourselves a
				// real chance of these tasks not getting scheduled back-to-back, which would block
				// the render loop.
				//
				// NOTE: This does not *SOLVE* the problem, it just makes the problem less likely
				// and less acute.
				//
				// NOTE 2: Lita asked me to explicitly state that we are not storing the access
				// token anywhere other than where the Auth0 library puts it.
				//
				await Promise.resolve();

				// Return.
				return update!();
			};
		}

		logger.info(`[${traceId}] function equality!!`, {
			isSameFunction: current.debuggingFunction === update,
			prev: current.debuggingFunction,
			update,
		});

		current.debuggingFunction = update;
		current.traceId = traceId;

		const newValue = { ...current }; // Trigger a re-render.
		logger.debug(`[${traceId}] Setting new getAccessTokenSilently function in atom.`, {
			newValue,
		});
		set($draftGetAccessTokenSilently, () => newValue);
		logger.debug(`[${traceId}] Setting new getAccessTokenSilently set`);
	}
);
$accessTokenFunc.debugLabel = "getAccessTokenSilently";

export const $isAuthenticated = atom<boolean>(false);
$isAuthenticated.debugLabel = "isAuthenticated";
export const $loggedInUser = atom<Auth0User | null>();
$loggedInUser.debugLabel = "loggedInUser";

export const $handleIsAuthenicated = atom(null, (_, set, isAuthenticated: boolean) => {
	set($isAuthenticated, isAuthenticated);
});
$handleIsAuthenicated.debugLabel = "handleIsAuthenicated";

export const $handleLoggedInUser = atom(null, (_, set, loggedInUser: Auth0User | null) => {
	set($loggedInUser, loggedInUser);
});
$handleLoggedInUser.debugLabel = "handleLoggedInUser";
